import React, { useState } from 'react';
import MicrositeLayout from '../MicrositeLayout.jsx';
import {
  getSocialUrl,
  extractYouTubeId,
  isYouTubeShort,
  linkIconMap,
  tipoEventoLabels,
  getFontById,
  isMicrositeSectionHidden,
  MICROSITE_SECTION_EMPTY_HINT,
  getMicrositeSectionLabel,
  getMicrositeHeroImageUrl,
  getCabeceraHeroVisibilidad,
  normalizeContactoMostrar,
  isMicrositeContactoSectionVisuallyEmpty,
  getMicrositeBodySectionOrder,
  micrositeCalendarioActuacionesVisibles,
  micrositeCalendarioEnsayosVisibles } from './micrositeUtils';
import { ScrollReveal,
  ScrollRevealSoft } from './MicrositeScrollReveal.jsx';
import MicrositePlantillaMusicos from './MicrositePlantillaMusicos.jsx';
import MicrositeLogrosBlock from './MicrositeLogrosBlock.jsx';
import MicrositeAvalesBlock from './MicrositeAvalesBlock.jsx';
import MicrositeResenasBlock from './MicrositeResenasBlock.jsx';
import MicrositeNoticiasBlock from './MicrositeNoticiasBlock.jsx';
import MicrositeCalendario from './MicrositeCalendario.jsx';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  Image,
  Icon,
  SimpleGrid,
  Flex,
  Wrap,
  WrapItem,
  Link,
  Button,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { Divider } from '@/components/ui/chakra-v2-compat';
import { Tooltip } from '@/components/ui/tooltip';
import {
  FaMapMarkerAlt, FaUsers, FaCalendarAlt, FaCheckCircle, FaStar, FaRegStar,
  FaMusic, FaYoutube, FaClock, FaChurch, FaInstagram, FaFacebook,
  FaEnvelope, FaPhone, FaExternalLinkAlt, FaQuoteLeft, FaArrowDown,
  FaGlobe, FaLink,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

const brutalShadow = '8px 8px 0 0 #000000';

const SectionHeading = ({ children, light, fontFamily, accentColor = '#facc15' }) => (
  <VStack gap={3} mb={{ base: 8, md: 12 }}>
    <Heading
      size={{ base: 'lg', md: 'xl' }}
      color={light ? 'white' : 'gray.900'}
      fontWeight="900"
      textAlign="center"
      letterSpacing="-0.03em"
      textTransform="uppercase"
      fontFamily={fontFamily || undefined}
    >
      {children}
    </Heading>
    <Box w="80px" h="6px" bg={light ? accentColor : 'black'} border="2px solid black" rounded="none" />
  </VStack>
);

const Section = ({ id, bg, children, py, sx }) => (
  <Box as="section" id={id} bg={bg || 'white'} py={py || { base: 16, md: 22 }} scrollMarginTop="60px" borderTop="4px solid black" sx={sx}>
    {children}
  </Box>
);

const MicrositePulso = ({
  bandaData, avales, stats, sections, msConfig, contacto, social, ensayosPublicos, eventosPublicos, actuacionesPublicas, locationText, hasSocials,
  primaryColor, secondaryColor = '#1a202c', surfaceColor = '#f7fafc',
}) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [showAllRepertoire, setShowAllRepertoire] = useState(false);
  const repertorioVisible = showAllRepertoire ? bandaData.repertorio : (bandaData.repertorio || []).slice(0, 15);
  const headingFont = getFontById(msConfig.tipografia).family || undefined;
  const secHidden = (id) => isMicrositeSectionHidden(id, msConfig);
  const enlacesItems = msConfig.enlacesPersonalizados || [];
  const heroImageUrl = getMicrositeHeroImageUrl(bandaData);
  const hv = getCabeceraHeroVisibilidad(msConfig);
  const heroHasSocials = hv.mostrarRrss && hasSocials;
  const musicosPlantilla = Array.isArray(bandaData.musicos) ? bandaData.musicos : [];
  const showPlantillaMusicos = msConfig.mostrarPlantillaMusicosNosotros === true && musicosPlantilla.length > 0;
  const showEspecialidadesWeb = msConfig.mostrarEspecialidadesNosotros !== false;
  const hasNosotrosContent =
    Boolean(bandaData.bio) ||
    (showEspecialidadesWeb && bandaData.especialidades?.length > 0) ||
    showPlantillaMusicos;
  const cm = normalizeContactoMostrar(msConfig.contactoMostrar);
  const contactoBlockEmpty = isMicrositeContactoSectionVisuallyEmpty(
    contacto,
    locationText,
    hasSocials,
    msConfig.textoContratar,
    msConfig,
  );

  const bodySectionOrder = getMicrositeBodySectionOrder(msConfig);
  const secSx = (id) => ({ order: bodySectionOrder.indexOf(id) });
  const calActsVis = micrositeCalendarioActuacionesVisibles(actuacionesPublicas, msConfig);
  const calEnsVis = micrositeCalendarioEnsayosVisibles(ensayosPublicos, msConfig);

  return (
    <MicrositeLayout bandaData={bandaData} micrositeConfig={msConfig} sections={sections} tema="pulso">

      {/* HERO — neo-brutalista */}
      <Box id="inicio" position="relative" minH="100vh" bg="white" borderBottom="6px solid black" pt={{ base: '88px', md: '96px' }} pb={16}>
        <Box bg={primaryColor} h="12px" borderBottom="4px solid black" w="full" />
        <Container maxW="5xl" position="relative" zIndex={1} textAlign="center" py={{ base: 12, md: 16 }}>
          <VStack gap={6}>
            <ScrollReveal>
              <Avatar.Root
                size="2xl"
                border="4px solid black"
                rounded="none"
                shadow={brutalShadow}
                w="140px"
                h="140px"><Avatar.Fallback name={bandaData.nombre} /><Avatar.Image src={bandaData.avatar} /></Avatar.Root>
            </ScrollReveal>
            <ScrollRevealSoft delay={0.06}>
              <VStack gap={2}>
                <HStack gap={2} justify="center" flexWrap="wrap">
                  <Heading size={{ base: '2xl', md: '3xl' }} color="gray.900" fontWeight="900" letterSpacing="-0.04em" lineHeight="1.05" fontFamily={headingFont} textTransform="uppercase">
                    {bandaData.nombre}
                  </Heading>
                  {hv.mostrarVerificado && bandaData.verificationStatus === 'VERIFIED' && (
                    <Tooltip content="Perfil verificado" showArrow>
                      <span>
                        <Icon color="green.600" w={7} h={7} asChild><FaCheckCircle /></Icon>
                      </span>
                    </Tooltip>
                  )}
                </HStack>
                {hv.mostrarUbicacion && locationText && (
                  <HStack gap={2} color="gray.700">
                    <Icon boxSize={4} asChild><FaMapMarkerAlt /></Icon>
                    <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="700">
                      {locationText}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </ScrollRevealSoft>
            <ScrollRevealSoft delay={0.12}>
              <Wrap gap={3} justify="center">
                {hv.mostrarNMusicos && bandaData.nMusicos && (
                  <WrapItem>
                    <Badge bg={primaryColor} color="black" px={4} py={2} rounded="none" fontSize="sm" fontWeight="900" border="2px solid black" boxShadow="4px 4px 0 #000">
                      <Icon mr={2} asChild><FaUsers /></Icon>
                      {bandaData.nMusicos} musicos
                    </Badge>
                  </WrapItem>
                )}
                {hv.mostrarAnoFundacion && bandaData.anoFundacion && (
                  <WrapItem>
                    <Badge bg={primaryColor} color="black" px={4} py={2} rounded="none" fontSize="sm" fontWeight="900" border="2px solid black" boxShadow="4px 4px 0 #000">
                      <Icon mr={2} asChild><FaCalendarAlt /></Icon>
                      Desde {bandaData.anoFundacion}
                    </Badge>
                  </WrapItem>
                )}
                {hv.mostrarTipoBanda && bandaData.tipoGrupo && (
                  <WrapItem>
                    <Badge bg={primaryColor} color="black" px={4} py={2} rounded="none" fontSize="sm" fontWeight="900" border="2px solid black" boxShadow="4px 4px 0 #000">
                      <Icon mr={2} asChild><FaMusic /></Icon>
                      {bandaData.tipoGrupo}
                    </Badge>
                  </WrapItem>
                )}
              </Wrap>
            </ScrollRevealSoft>
            {heroHasSocials && (
              <ScrollRevealSoft delay={0.18} y={16} amount={0.14}>
                <HStack gap={4} mt={2}>
                  {social.instagram && (
                    <Link
                      href={getSocialUrl('instagram', social.instagram)}
                      target='_blank'
                      rel='noopener noreferrer'>
                      <Icon
                        boxSize={7}
                        color="gray.800"
                        _hover={{ color: primaryColor, transform: 'scale(1.1)' }}
                        transition="all 0.2s"
                        asChild><FaInstagram /></Icon>
                    </Link>
                  )}
                  {social.facebook && (
                    <Link
                      href={getSocialUrl('facebook', social.facebook)}
                      target='_blank'
                      rel='noopener noreferrer'>
                      <Icon
                        boxSize={7}
                        color="gray.800"
                        _hover={{ color: primaryColor, transform: 'scale(1.1)' }}
                        transition="all 0.2s"
                        asChild><FaFacebook /></Icon>
                    </Link>
                  )}
                  {social.youtube && (
                    <Link
                      href={getSocialUrl('youtube', social.youtube)}
                      target='_blank'
                      rel='noopener noreferrer'>
                      <Icon
                        boxSize={7}
                        color="gray.800"
                        _hover={{ color: primaryColor, transform: 'scale(1.1)' }}
                        transition="all 0.2s"
                        asChild><FaYoutube /></Icon>
                    </Link>
                  )}
                  {social.twitter && (
                        <Link
                          href={getSocialUrl('twitter', social.twitter)}
                          target='_blank'
                          rel='noopener noreferrer'>
                          <Icon boxSize={7} color="gray.800" _hover={{ color: primaryColor, transform: 'scale(1.1)' }} transition="all 0.2s"  asChild><FaXTwitter /></Icon>
                        </Link>
                      )}
                </HStack>
              </ScrollRevealSoft>
            )}
            <ScrollRevealSoft delay={heroHasSocials ? 0.24 : 0.18}>
              <Button
                mt={4}
                size="lg"
                rounded="none"
                px={10}
                bg={primaryColor}
                color="black"
                fontWeight="900"
                border="3px solid black"
                boxShadow="6px 6px 0 #000"
                _hover={{ transform: 'translate(2px, 2px)', boxShadow: '4px 4px 0 #000' }}
                transition="all 0.15s ease"
                onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
              >
                CONTACTANOS
              </Button>
            </ScrollRevealSoft>
            <ScrollRevealSoft delay={heroHasSocials ? 0.3 : 0.24}>
              <Icon
                color="gray.600"
                boxSize={6}
                mt={4}
                animation="bounce 2s infinite"
                css={{
                  '& @keyframes bounce': { '0%,100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(8px)' } }
                }}
                asChild><FaArrowDown /></Icon>
            </ScrollRevealSoft>
          </VStack>
        </Container>
      </Box>

      <Flex direction="column" w="full">
      {/* SOBRE NOSOTROS */}
      {!secHidden('nosotros') && (
        <Section id="nosotros" sx={secSx('nosotros')}>
          <Container maxW="4xl">
            <ScrollReveal>
              <SectionHeading fontFamily={headingFont} accentColor={primaryColor}>{getMicrositeSectionLabel('nosotros')}</SectionHeading>
            </ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {hasNosotrosContent ? (
                <>
                  {bandaData.bio && (
                    <Text fontSize={{ base: 'md', md: 'lg' }} lineHeight="1.8" color="gray.600" textAlign="center" whiteSpace="pre-line" maxW="3xl" mx="auto">{bandaData.bio}</Text>
                  )}
                  {showEspecialidadesWeb && bandaData.especialidades?.length > 0 && (
                    <Wrap gap={3} justify="center" mt={bandaData.bio ? 8 : 0}>
                      {bandaData.especialidades.map((esp, i) => <WrapItem key={i}><Badge colorPalette="purple" variant="subtle" px={4} py={2} rounded="full" fontSize="sm" fontWeight="600">{esp}</Badge></WrapItem>)}
                    </Wrap>
                  )}
                  {showPlantillaMusicos && (
                    <MicrositePlantillaMusicos musicos={musicosPlantilla} micrositeConfig={msConfig} primaryColor={primaryColor} variant="pulso" />
                  )}
                </>
              ) : (
                <Text fontSize={{ base: 'md', md: 'lg' }} lineHeight="1.8" color="gray.500" textAlign="center" maxW="3xl" mx="auto">{MICROSITE_SECTION_EMPTY_HINT.nosotros}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </Section>
      )}

      {/* HISTORIA */}
      {!secHidden('historia') && (
        <Section id="historia" bg={surfaceColor} sx={secSx('historia')}>
          <Container maxW="4xl">
            <ScrollReveal>
              <SectionHeading fontFamily={headingFont} accentColor={primaryColor}>{getMicrositeSectionLabel('historia')}</SectionHeading>
            </ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {msConfig.historia ? (
                <Text fontSize={{ base: 'md', md: 'lg' }} lineHeight="1.9" color="gray.600" textAlign="center" whiteSpace="pre-line">{msConfig.historia}</Text>
              ) : (
                <Text fontSize={{ base: 'md', md: 'lg' }} lineHeight="1.8" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.historia}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </Section>
      )}

      {/* NOTICIAS */}
      {!secHidden('noticias') && (
        <Section id="noticias" bg={surfaceColor} sx={secSx('noticias')}>
          <Container maxW="4xl">
            <ScrollReveal>
              <SectionHeading fontFamily={headingFont} accentColor={primaryColor}>{getMicrositeSectionLabel('noticias')}</SectionHeading>
            </ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              <MicrositeNoticiasBlock msConfig={msConfig} primaryColor={primaryColor} />
            </ScrollRevealSoft>
          </Container>
        </Section>
      )}

      {/* DIRECTIVA */}
      {!secHidden('directiva') && (
        <Section id="directiva" sx={secSx('directiva')}>
          <Container maxW="5xl">
            <ScrollReveal>
              <SectionHeading fontFamily={headingFont} accentColor={primaryColor}>{getMicrositeSectionLabel('directiva')}</SectionHeading>
            </ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {(msConfig.directiva?.length > 0) ? (
                <Flex flexWrap="wrap" justify="center" align="flex-start" gap={{ base: 6, md: 8 }} w="full">
                  {msConfig.directiva.map((m, i) => (
                    <VStack key={m.id || i} gap={3} textAlign="center" align="center" minW="140px" maxW="220px" flex="0 0 auto">
                      <Avatar.Root size="md" bg="purple.100" color="purple.600"><Avatar.Fallback name={m.nombre} /><Avatar.Image src={m.foto || undefined} /></Avatar.Root>
                      <VStack gap={0}><Text fontWeight="700" fontSize="sm" color="gray.800">{m.nombre}</Text><Text fontSize="xs" color="purple.500" fontWeight="600">{m.cargo}</Text></VStack>
                    </VStack>
                  ))}
                </Flex>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.directiva}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </Section>
      )}

      {/* GALERIA */}
      {!secHidden('galeria') && (
        <Section id="galeria" bg={surfaceColor} sx={secSx('galeria')}>
          <Container maxW="6xl">
            <ScrollReveal>
              <SectionHeading fontFamily={headingFont} accentColor={primaryColor}>{getMicrositeSectionLabel('galeria')}</SectionHeading>
            </ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {bandaData.fotos?.length > 0 ? (
                <Flex flexWrap="wrap" justifyContent="center" alignItems="stretch" gap={4} w="full">
                  {bandaData.fotos.map((foto, i) => (
                    <Box key={i} flex={{ base: '1 1 100%', sm: '0 1 calc(50% - 8px)', lg: '0 1 300px' }} maxW={{ base: '100%', sm: '440px', lg: '320px' }} minW={0} position="relative" overflow="hidden" rounded="xl" cursor="pointer" onClick={() => setSelectedImage(foto)} role="group">
                      <Image src={foto} alt={`Foto ${i + 1}`} w="full" h={{ base: '220px', md: '280px' }} objectFit="cover" transition="transform 0.4s ease" _groupHover={{ transform: 'scale(1.05)' }} />
                      <Box position="absolute" inset={0} bg="blackAlpha.400" opacity={0} _groupHover={{ opacity: 1 }} transition="opacity 0.3s" display="flex" alignItems="center" justifyContent="center">
                        <Icon color="white" boxSize={6} asChild><FaExternalLinkAlt /></Icon>
                      </Box>
                    </Box>
                  ))}
                </Flex>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.galeria}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </Section>
      )}

      {/* VIDEOS */}
      {!secHidden('videos') && (
        <Section id="videos" sx={secSx('videos')}>
          <Container maxW="5xl">
            <ScrollReveal>
              <SectionHeading fontFamily={headingFont} accentColor={primaryColor}>{getMicrositeSectionLabel('videos')}</SectionHeading>
            </ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {bandaData.videos?.length > 0 ? (() => {
                const normalVideos = bandaData.videos.filter((v) => !isYouTubeShort(v.url));
                const shorts = bandaData.videos.filter((v) => isYouTubeShort(v.url));
                const iframeAllow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
                return (
                  <VStack gap={10} align="center" w="full">
                    {normalVideos.length > 0 && (
                      <Box w="full">
                        <Text fontSize="xs" fontWeight="700" color="gray.500" mb={3} textTransform="uppercase" letterSpacing="0.06em" textAlign="center" w="full">
                          Vídeos
                        </Text>
                        <Flex flexWrap="wrap" justifyContent="center" alignItems="stretch" gap={6} w="full" maxW="960px" mx="auto">
                          {normalVideos.map((video, i) => {
                            const videoId = extractYouTubeId(video.url);
                            return (
                              <Box key={video.id ?? `v-n-${i}`} flex={{ base: '1 1 100%', md: '0 1 440px' }} maxW={{ base: '100%', md: 'min(100%, 460px)' }} minW={0} rounded="xl" overflow="hidden" shadow="lg" bg="white">
                                {videoId ? (
                                  <Box position="relative" pb="56.25%">
                                    <iframe src={`https://www.youtube.com/embed/${videoId}`} title={video.titulo} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} frameBorder="0" allow={iframeAllow} allowFullScreen />
                                  </Box>
                                ) : (
                                  <Box h="180px" bg="gray.100" display="flex" alignItems="center" justifyContent="center">
                                    <Icon w={12} h={12} color="gray.300" asChild><FaYoutube /></Icon>
                                  </Box>
                                )}
                                {video.titulo && (
                                  <Box p={4}>
                                    <Text fontSize="sm" fontWeight="600" color="gray.700" textAlign="center">
                                      {video.titulo}
                                    </Text>
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Flex>
                      </Box>
                    )}
                    {shorts.length > 0 && (
                      <Box w="full">
                        <Text fontSize="xs" fontWeight="700" color="gray.500" mb={3} textTransform="uppercase" letterSpacing="0.06em" textAlign="center" w="full">
                          Shorts
                        </Text>
                        <Flex flexWrap="wrap" justifyContent="center" alignItems="flex-start" gap={{ base: 3, md: 4 }} rowGap={5} w="full">
                          {shorts.map((video, i) => {
                            const videoId = extractYouTubeId(video.url);
                            return (
                              <Box key={video.id ?? `v-s-${i}`} flex="0 0 auto" w={{ base: 'calc(50% - 6px)', sm: '200px', md: '240px' }} maxW="260px" display="flex" flexDirection="column" alignItems="center">
                                <Box rounded="xl" overflow="hidden" shadow="md" bg="white" w="full" maxW="260px">
                                  {videoId ? (
                                    <Box position="relative" pb="177.78%" w="full">
                                      <iframe src={`https://www.youtube.com/embed/${videoId}`} title={video.titulo} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} frameBorder="0" allow={iframeAllow} allowFullScreen />
                                    </Box>
                                  ) : (
                                    <Box h="120px" bg="gray.100" display="flex" alignItems="center" justifyContent="center">
                                      <Icon w={8} h={8} color="gray.300" asChild><FaYoutube /></Icon>
                                    </Box>
                                  )}
                                  {video.titulo && (
                                    <Box p={3}>
                                      <Text fontSize="xs" fontWeight="600" color="gray.700" lineClamp={2} textAlign="center">
                                        {video.titulo}
                                      </Text>
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Flex>
                      </Box>
                    )}
                  </VStack>
                );
              })() : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.videos}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </Section>
      )}

      {/* REPERTORIO */}
      {!secHidden('repertorio') && (
        <Section id="repertorio" bg={surfaceColor} sx={secSx('repertorio')}>
          <Container maxW="4xl">
            <ScrollReveal>
              <SectionHeading fontFamily={headingFont} accentColor={primaryColor}>{getMicrositeSectionLabel('repertorio')}</SectionHeading>
            </ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {bandaData.repertorio?.length > 0 ? (
                <>
                  <Text textAlign="center" color="gray.500" fontSize="sm" mb={6}>{bandaData.repertorio.length} obras</Text>
                  <Box rounded="xl" overflow="hidden" shadow="md" bg="white">
                    {repertorioVisible.map((item, i) => {
                      const nombre = item.nombre || item.marcha?.nombre || '';
                      const autor = item.autor || item.marcha?.autor || '';
                      const tipo = item.tipo || item.marcha?.tipo || '';
                      return (
                        <Flex key={item.id || i} px={5} py={3} align="center" bg={i % 2 === 0 ? 'white' : surfaceColor} borderBottom={i < repertorioVisible.length - 1 ? '1px solid' : 'none'} borderColor="gray.100" _hover={{ bg: `${primaryColor}18` }} transition="background 0.2s">
                          <Text fontSize="sm" fontWeight="600" color="gray.300" minW="35px">{i + 1}</Text>
                          <VStack align="start" gap={0} flex={1}>
                            <Text fontWeight="600" fontSize="sm" color="gray.800">{nombre}</Text>
                            <HStack gap={2}>{autor && <Text fontSize="xs" color="gray.500">{autor}</Text>}{autor && tipo && <Text fontSize="xs" color="gray.300">·</Text>}{tipo && <Badge colorPalette="purple" variant="subtle" fontSize="10px">{tipo}</Badge>}</HStack>
                          </VStack>
                        </Flex>
                      );
                    })}
                  </Box>
                  {bandaData.repertorio.length > 15 && !showAllRepertoire && (
                    <Flex justify="center" mt={4}><Button variant="outline" colorPalette="purple" size="sm" rounded="full" onClick={() => setShowAllRepertoire(true)}>Ver todo el repertorio ({bandaData.repertorio.length} obras)</Button></Flex>
                  )}
                </>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.repertorio}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </Section>
      )}

      {/* LOGROS */}
      {!secHidden('logros') && (
        <Section id="logros" sx={secSx('logros')}>
          <Container maxW="5xl">
            <ScrollReveal>
              <SectionHeading fontFamily={headingFont} accentColor={primaryColor}>{getMicrositeSectionLabel('logros')}</SectionHeading>
            </ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {(msConfig.logros?.length > 0) ? (
                <MicrositeLogrosBlock logros={msConfig.logros} primaryColor={primaryColor} surfaceColor={surfaceColor} variant="pulso" />
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.logros}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </Section>
      )}

      {/* AVALES Y RESENAS */}
      {!secHidden('resenas') && (
        <Section id="resenas" bg={surfaceColor} sx={secSx('resenas')}>
          <Container maxW="5xl">
            <ScrollReveal>
              <SectionHeading fontFamily={headingFont} accentColor={primaryColor}>{getMicrositeSectionLabel('resenas')}</SectionHeading>
            </ScrollReveal>
            {(avales.length > 0 || (bandaData.resenasRecibidas?.filter(r => r.isPublished).length > 0)) ? (
            <>
            {avales.length > 0 && (
              <ScrollRevealSoft delay={0.08}>
                <MicrositeAvalesBlock
                  avales={avales}
                  variant="moderno"
                  surfaceColor={surfaceColor}
                  mb={bandaData.resenasRecibidas?.filter(r => r.isPublished).length > 0 ? 10 : 0}
                />
              </ScrollRevealSoft>
            )}
            {bandaData.resenasRecibidas?.filter(r => r.isPublished).length > 0 && (
              <ScrollRevealSoft delay={avales.length > 0 ? 0.15 : 0.08}>
                <MicrositeResenasBlock
                  resenas={bandaData.resenasRecibidas.filter((r) => r.isPublished)}
                  stats={stats}
                  variant="moderno"
                  primaryColor={primaryColor}
                  surfaceColor={surfaceColor}
                />
              </ScrollRevealSoft>
            )}
            </>
            ) : (
              <ScrollRevealSoft delay={0.08}>
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.resenas}</Text>
              </ScrollRevealSoft>
            )}
          </Container>
        </Section>
      )}

      {/* CALENDARIO */}
      {!secHidden('calendario') && (
        <Section id="calendario" bg={primaryColor} py={{ base: 16, md: 20 }} sx={secSx('calendario')}>
          <Container maxW="5xl">
            <ScrollReveal>
              <SectionHeading light fontFamily={headingFont} accentColor="rgba(255,255,255,0.85)">{getMicrositeSectionLabel('calendario')}</SectionHeading>
            </ScrollReveal>
            {(((msConfig.calendarioMostrarActuaciones !== false) && calActsVis.length > 0) || ((msConfig.calendarioMostrarEnsayos !== false) && calEnsVis.length > 0)) ? (
              <ScrollRevealSoft delay={0.08}>
                <MicrositeCalendario
                  msConfig={msConfig}
                  actuaciones={(msConfig.calendarioMostrarActuaciones !== false) ? calActsVis : []}
                  ensayos={(msConfig.calendarioMostrarEnsayos !== false) ? calEnsVis : []}
                  light
                />
              </ScrollRevealSoft>
            ) : (
              <ScrollRevealSoft delay={0.08}>
                <Text color="whiteAlpha.700" fontSize="md" textAlign="center" maxW="lg" mx="auto">{MICROSITE_SECTION_EMPTY_HINT.calendario}</Text>
              </ScrollRevealSoft>
            )}
          </Container>
        </Section>
      )}

      {/* PATROCINADORES */}
      {!secHidden('patrocinadores') && (
        <Section id="patrocinadores" sx={secSx('patrocinadores')}>
          <Container maxW="5xl">
            <ScrollReveal>
              <SectionHeading fontFamily={headingFont} accentColor={primaryColor}>{getMicrositeSectionLabel('patrocinadores')}</SectionHeading>
            </ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {(msConfig.patrocinadores?.length > 0) ? (
                <Flex wrap="wrap" justify="center" gap={8}>
                  {msConfig.patrocinadores.map((p, i) => {
                    const content = p.logo ? <Image src={p.logo} alt={p.nombre} h="60px" w="auto" maxW="160px" objectFit="contain" filter="grayscale(100%)" _hover={{ filter: 'grayscale(0%)' }} transition="filter 0.3s" /> : <Text fontSize="sm" fontWeight="600" color="gray.600">{p.nombre}</Text>;
                    return (
                      <VStack key={i} gap={2}>
                        {p.url ? <Link
                          href={p.url.startsWith('http') ? p.url : `https://${p.url}`}
                          _hover={{ opacity: 0.8 }}
                          target='_blank'
                          rel='noopener noreferrer'>{content}</Link> : content}
                        <Text fontSize="xs" color="gray.400">{p.nombre}</Text>
                      </VStack>
                    );
                  })}
                </Flex>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.patrocinadores}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </Section>
      )}

      {/* ENLACES (bloque propio; el menú ancla a #enlaces) */}
      {!secHidden('enlaces') && (
        <Section id="enlaces" bg={surfaceColor} sx={secSx('enlaces')}>
          <Container maxW="4xl">
            <ScrollReveal>
              <SectionHeading fontFamily={headingFont} accentColor={primaryColor}>{getMicrositeSectionLabel('enlaces')}</SectionHeading>
            </ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {enlacesItems.length > 0 ? (
                <VStack gap={3} align="center">
                  {enlacesItems.map((e, i) => {
                    const LinkIcon = linkIconMap[e.icono] || FaLink;
                    return (
                      <Link
                        key={i}
                        href={e.url?.startsWith('http') ? e.url : `https://${e.url || ''}`}
                        target='_blank'
                        rel='noopener noreferrer'>
                        <HStack gap={2} color={primaryColor}><Icon boxSize={4} asChild><LinkIcon /></Icon><Text fontSize="sm" fontWeight="600">{e.titulo || e.url}</Text></HStack>
                      </Link>
                    );
                  })}
                </VStack>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.enlaces}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </Section>
      )}

      {/* CONTACTO */}
      {!secHidden('contacto') && (
      <Section id="contacto" bg={secondaryColor} py={{ base: 16, md: 24 }} sx={secSx('contacto')}>
        <Container maxW="4xl">
          <ScrollReveal>
            <SectionHeading light fontFamily={headingFont} accentColor="rgba(255,255,255,0.85)">{getMicrositeSectionLabel('contacto')}</SectionHeading>
          </ScrollReveal>
          {msConfig.textoContratar && cm.textoContratar && (
            <ScrollRevealSoft delay={0.08}>
              <Text color="whiteAlpha.700" textAlign="center" fontSize={{ base: 'md', md: 'lg' }} lineHeight="1.8" mb={10} maxW="3xl" mx="auto">{msConfig.textoContratar}</Text>
            </ScrollRevealSoft>
          )}
          <ScrollRevealSoft delay={msConfig.textoContratar && cm.textoContratar ? 0.14 : 0.08}>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={8}>
              <VStack align={{ base: 'center', md: 'start' }} gap={4}>
                {contacto.email && cm.email && <Link href={`mailto:${contacto.email}`} _hover={{ textDecoration: 'none' }}><HStack gap={3} _hover={{ opacity: 0.8 }} transition="opacity 0.2s"><Flex w="40px" h="40px" rounded="full" bg="whiteAlpha.100" align="center" justify="center"><Icon color="purple.300" asChild><FaEnvelope /></Icon></Flex><Text color="whiteAlpha.900" fontSize="md">{contacto.email}</Text></HStack></Link>}
                {contacto.telefono && cm.telefono && <Link href={`tel:${contacto.telefono}`} _hover={{ textDecoration: 'none' }}><HStack gap={3} _hover={{ opacity: 0.8 }} transition="opacity 0.2s"><Flex w="40px" h="40px" rounded="full" bg="whiteAlpha.100" align="center" justify="center"><Icon color="purple.300" asChild><FaPhone /></Icon></Flex><Text color="whiteAlpha.900" fontSize="md">{contacto.telefono}</Text></HStack></Link>}
                {contacto.website && cm.website && <Link
                  href={contacto.website.startsWith('http') ? contacto.website : `https://${contacto.website}`}
                  target='_blank'
                  rel='noopener noreferrer'><HStack gap={3} _hover={{ opacity: 0.8 }} transition="opacity 0.2s"><Flex w="40px" h="40px" rounded="full" bg="whiteAlpha.100" align="center" justify="center"><Icon color="purple.300" asChild><FaGlobe /></Icon></Flex><Text color="whiteAlpha.900" fontSize="md">{contacto.website}</Text></HStack></Link>}
                {locationText && cm.ubicacion && <HStack gap={3}><Flex w="40px" h="40px" rounded="full" bg="whiteAlpha.100" align="center" justify="center"><Icon color="purple.300" asChild><FaMapMarkerAlt /></Icon></Flex><Text color="whiteAlpha.900" fontSize="md">{locationText}</Text></HStack>}
              </VStack>
              <VStack align={{ base: 'center', md: 'start' }} gap={4}>
                {hasSocials && cm.redes && (
                  <>
                    <Text color="whiteAlpha.500" fontWeight="700" fontSize="xs" textTransform="uppercase" letterSpacing="wider">Redes Sociales</Text>
                    <HStack gap={4}>
                      {social.instagram && <Link
                        href={getSocialUrl('instagram', social.instagram)}
                        target='_blank'
                        rel='noopener noreferrer'><Flex w="48px" h="48px" rounded="full" bg="whiteAlpha.100" align="center" justify="center" _hover={{ bg: 'whiteAlpha.200', transform: 'scale(1.1)' }} transition="all 0.2s"><Icon boxSize={5} color="whiteAlpha.900" asChild><FaInstagram /></Icon></Flex></Link>}
                      {social.facebook && <Link
                        href={getSocialUrl('facebook', social.facebook)}
                        target='_blank'
                        rel='noopener noreferrer'><Flex w="48px" h="48px" rounded="full" bg="whiteAlpha.100" align="center" justify="center" _hover={{ bg: 'whiteAlpha.200', transform: 'scale(1.1)' }} transition="all 0.2s"><Icon boxSize={5} color="whiteAlpha.900" asChild><FaFacebook /></Icon></Flex></Link>}
                      {social.youtube && <Link
                        href={getSocialUrl('youtube', social.youtube)}
                        target='_blank'
                        rel='noopener noreferrer'><Flex w="48px" h="48px" rounded="full" bg="whiteAlpha.100" align="center" justify="center" _hover={{ bg: 'whiteAlpha.200', transform: 'scale(1.1)' }} transition="all 0.2s"><Icon boxSize={5} color="whiteAlpha.900" asChild><FaYoutube /></Icon></Flex></Link>}
{social.twitter && <Link
                        href={getSocialUrl('twitter', social.twitter)}
                        target='_blank'
                        rel='noopener noreferrer'><Flex w="48px" h="48px" rounded="full" bg="whiteAlpha.100" align="center" justify="center" _hover={{ bg: 'whiteAlpha.200', transform: 'scale(1.1)' }} transition="all 0.2s"><Icon boxSize={5} color="whiteAlpha.900" asChild><FaXTwitter /></Icon></Flex></Link>}
                    </HStack>
                  </>
                )}
              </VStack>
            </SimpleGrid>
          </ScrollRevealSoft>
          {contactoBlockEmpty && (
            <ScrollRevealSoft delay={0.08}>
              <Text color="whiteAlpha.600" fontSize="md" textAlign="center" mt={6}>{MICROSITE_SECTION_EMPTY_HINT.contacto}</Text>
            </ScrollRevealSoft>
          )}
        </Container>
      </Section>
      )}
      </Flex>

      {/* Image lightbox */}
      <Dialog.Root open={!!selectedImage} size='full' placement='center' onOpenChange={e => {
        if (!e.open) {
          setSelectedImage(null);
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.900" />
          <Dialog.Positioner>
            <Dialog.Content bg="transparent" boxShadow="none">
              <Dialog.CloseTrigger color="white" size="lg" bg="blackAlpha.600" _hover={{ bg: 'blackAlpha.700' }} rounded="full" m={4} zIndex={10} />
              <Dialog.Body display="flex" alignItems="center" justifyContent="center" p={4} onClick={() => setSelectedImage(null)}>
                {selectedImage && <Image src={selectedImage} alt="Imagen" maxH="90vh" maxW="90vw" objectFit="contain" onClick={(e) => e.stopPropagation()} />}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </MicrositeLayout>
  );
};

export default MicrositePulso;
